import React from 'react';
import { useForm } from '@inertiajs/react';
import { Pencil, LogOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Organization {
  name: string;
  slug: string;
  members_count: number;
  is_owner: boolean;
  created_at: string;
}

interface OrganizationHeaderProps {
  organization: Organization;
  canManage: boolean;
  onLeaveClick: () => void;
}

export function OrganizationHeader({ organization, canManage, onLeaveClick }: OrganizationHeaderProps) {
  const [editingName, setEditingName] = React.useState(false);

  const { data: nameData, setData: setNameData, patch: updateName, processing: nameProcessing, errors: nameErrors } = useForm({
    organization: { name: organization.name }
  });

  const handleNameUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    updateName(`/dashboard/organizations/${organization.slug}`, {
      onSuccess: () => setEditingName(false)
    });
  };

  return (
    <div className="space-y-6">
      {/* Title row */}
      <div className="flex items-start justify-between">
        <div>
          {editingName ? (
            <form onSubmit={handleNameUpdate} className="space-y-2">
              <Input
                value={nameData.organization.name}
                onChange={(e) => setNameData('organization', { ...nameData.organization, name: e.target.value })}
                autoFocus
                className="text-2xl font-semibold tracking-tight h-auto py-1 px-2"
                data-testid="organization-name-input"
                aria-invalid={!!nameErrors['organization.name']}
              />
              {nameErrors['organization.name'] && (
                <p className="text-sm text-destructive">{nameErrors['organization.name']}</p>
              )}
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={nameProcessing} aria-busy={nameProcessing}>
                  {nameProcessing && <Loader2 className="animate-spin" />}
                  {nameProcessing ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingName(false);
                    setNameData('organization', { ...nameData.organization, name: organization.name });
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{organization.name}</h1>
              {canManage && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setEditingName(true)}
                  data-testid="edit-organization-name"
                  aria-label="Editar nome da organização"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            {organization.members_count} {organization.members_count === 1 ? 'membro' : 'membros'} · Criada em {new Date(organization.created_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
        {!organization.is_owner && (
          <Button variant="outline" size="sm" onClick={onLeaveClick}>
            <LogOut className="w-4 h-4" />
            Sair da Organização
          </Button>
        )}
      </div>
    </div>
  );
}
